import { FC, Fragment, useState } from 'react';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';
import {
	Button,
	Col,
	Collapse,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Typography,
} from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import CCAVisitReportAttendee from './VisitReportVerificationPage.Office.Attendee';
import CCAVisitReportForm, {
	TechnicalAspectForm,
	useCCAVisitReportForm,
} from './VisitReportVerificationPage.Office.Form';
import CCAVisitReportUnits from './VisitReportVerificationPage.Office.Unit';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	MarketingAspect,
	OfficeObservationCollateralAspect,
	OfficeObservationWarehouse,
	TechnicalAspect,
} from '@/libs/fleet-api/services/types';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import CCAisitReportSalesMethod from './VisitReportVerificationPage.Office.Method';
import CCAVisitReportPaymentSystem from './VisitReportVerificationPage.Office.Payment';
import CCAVisitReportCompetitor from './VisitReportVerificationPage.Office.Competitor';
import CCAVisitReportBouwheer from './VisitReportVerificationPage.Office.Bouwheer';
import CCAVisitReportWarehouse, {
	useWarehouseForm,
} from './VisitReportVerificationPage.Office.Warehouse';
import CCAVisitReportSparePart from './VisitReportVerificationPage.Office.SparePart';
import CCAVisitReportInventory from './VisitReportVerificationPage.Office.Inventory';
import { useSelectMaterialOwnershipType } from '@/libs/fleet-api/hooks/master-data';
import { OfficeMaterialOwnership } from '@/libs/fleet-api/enums';
import CCAVisitReportParetoSupplier from './VisitReportVerificationPage.Office.ParetoSupplier';
import CCAVisitReportParetoReceivable from './VisitReportVerificationPage.Office.Pareto';
import CCAVisitReportOfficeCollateralUnit from './VisitReportVerificationPage.Office.CollateralUnit';
import CCAVisitReportPhoto from './VisitReportVerificationPage.Office.Photo';
import { useMutation } from '@tanstack/react-query';
import {
	submitCCAOfficeObservationCollateralAspect,
	submitCCAOfficeObservationMarketingAspect,
	submitCCAOfficeObservationWarehouse,
} from '@/libs/fleet-api/services/visit-reports';
import { inputCurrency } from '@/libs/antd/input-number';
import { useApp } from '@/App';

const CCAVisitReportVerificationOffice: FC<{ onNext: () => void }> = ({
	onNext,
}) => {
	const { notification } = useApp();
	const { officeObservation, surveyInformation } =
		useCCAVisitReportVerificationPage();
	const { technicalAspect, marketingAspect, warehouse, collateral } =
		officeObservation;
	const { technicalAspectForm, technicalAspectMutation } =
		useCCAVisitReportForm();

	const marketingAspectForm = useForm<MarketingAspect>();
	const warehouseForm = useWarehouseForm();
	const collateralForm = useForm<OfficeObservationCollateralAspect>();

	const selectMaterialOwnershipType = useSelectMaterialOwnershipType();

	const submitCCAOfficeObservationMarketingAspectMutation = useMutation({
		mutationFn: submitCCAOfficeObservationMarketingAspect,
	});

	const submitCCAOfficeObservationWarehouseMutation = useMutation({
		mutationFn: submitCCAOfficeObservationWarehouse,
	});

	const submitCCAOfficeObservationCollateralAspectMutation = useMutation({
		mutationFn: submitCCAOfficeObservationCollateralAspect,
	});

	const [activeKey, setActiveKey] = useState<string[]>([]);
	const [checkedItems, setCheckedItems] = useState<string[]>([]);

	const renderCheck = (key: string) => {
		return (
			<span
				style={{
					fontSize: 24,
					lineHeight: 1,
					color: checkedItems.includes(key) ? '#39A905' : '#9A9A9A',
				}}
			>
				<CheckCircleFilled />
			</span>
		);
	};

	const handleChecked = (key: string) => {
		setCheckedItems((checked) => [...checked, key]);
		setActiveKey((keys) => keys.filter((f) => f !== key));
	};

	const isAllowSubmitting =
		checkedItems.includes('technicalAspect') &&
		checkedItems.includes('marketingAspect') &&
		checkedItems.includes('workshop') &&
		checkedItems.includes('collateral');

	return (
		<Fragment>
			<Collapse
				activeKey={activeKey}
				onChange={(keys) => setActiveKey(keys as string[])}
				items={[
					{
						key: 'technicalAspect',
						label: 'Aspek Teknis',
						extra: renderCheck('technicalAspect'),
						children: (
							<Form.Provider
								onFormFinish={async (name, { values }) => {
									if (!name) return;
									try {
										await technicalAspectMutation.mutateAsync({
											...(values as TechnicalAspect<'payload'>),
											surveyId: surveyInformation.id!,
											id: surveyInformation.id!,
											unitDocumentFile: values.unitDocumentFile && {
												id: values.unitDocumentFile.id,
												name: values.unitDocumentFile.name,
												path: values.unitDocumentFile.path,
											},
										});
										handleChecked('technicalAspect');
									} catch {
										notification.error({
											message: 'Failed',
											description: 'Failed submit technical aspect',
										});
									}
								}}
							>
								<CCAVisitReportAttendee />
								<CCAVisitReportForm
									technicalAspectForm={technicalAspectForm}
									technicalAspectMutation={technicalAspectMutation}
								/>
								<CCAVisitReportUnits />
								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									name="technicalAspectNotes"
									form={technicalAspectForm.form}
									labelCol={{
										span: 10,
									}}
									initialValues={
										{
											technicalAspectNotes:
												technicalAspect?.technicalAspectNotes,
											technicalAspectVerificationNotes:
												technicalAspect?.technicalAspectVerificationNotes,
										} as TechnicalAspectForm
									}
								>
									<Form.Item
										label="FRM Notes"
										name={technicalAspectForm.fieldName('technicalAspectNotes')}
										labelCol={{
											span: 6,
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
									>
										{technicalAspectForm.form.getFieldValue(
											technicalAspectForm.fieldName('technicalAspectNotes'),
										) ?? '-'}
									</Form.Item>
									<Divider />
									<Form.Item
										name={technicalAspectForm.fieldName(
											'technicalAspectVerificationNotes',
										)}
										label="Verification Notes"
										labelCol={{
											span: 6,
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
										rules={[rule.required()]}
									>
										<Input.TextArea rows={4} />
									</Form.Item>
								</Form>
								<Button
									type="primary"
									loading={technicalAspectMutation.isPending}
									style={{
										width: 200,
										display: 'block',
										marginLeft: 'auto',
										marginTop: 30,
									}}
									onClick={() => {
										technicalAspectForm.form.submit();
									}}
								>
									SAVE
								</Button>
							</Form.Provider>
						),
					},
					{
						key: 'marketingAspect',
						label: 'Aspek Pemasaran',
						collapsible: !checkedItems.includes('technicalAspect')
							? 'disabled'
							: undefined,
						extra: renderCheck('marketingAspect'),
						children: (
							<Form.Provider
								onFormFinish={async (name, { values }) => {
									if (!name) return;
									try {
										await submitCCAOfficeObservationMarketingAspectMutation.mutateAsync(
											{
												...(values as MarketingAspect),
												surveyId: marketingAspect.surveyId,
											},
										);
										handleChecked('marketingAspect');
									} catch {
										notification.error({
											message: 'Failed',
											description: 'Failed submit marketing aspect',
										});
									}
								}}
							>
								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={marketingAspectForm.form}
									name="productOffer"
									initialValues={
										{
											productOffer: marketingAspect?.productOffer,
										} as MarketingAspect
									}
								>
									<Row gutter={[20, 40]}>
										<Col span={12}>
											<Form.Item
												name={marketingAspectForm.fieldName('productOffer')}
												label="Produk/Jasa yang dipasarkan"
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</Form>
								<Divider />

								<CCAisitReportSalesMethod />
								<CCAVisitReportPaymentSystem />

								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={marketingAspectForm.form}
									name="isOtherBusiness"
									initialValues={
										{
											isOtherBusiness: marketingAspect?.isOtherBusiness,
											otherBusiness: marketingAspect?.otherBusiness,
											otherBusinessPortion:
												marketingAspect?.otherBusinessPortion,
										} as MarketingAspect
									}
								>
									<Row gutter={[20, 40]}>
										<Col span={12}>
											<Form.Item
												name={marketingAspectForm.fieldName('isOtherBusiness')}
												label="Apakah terdapat usaha lain?"
												rules={[rule.required()]}
											>
												<Radio.Group style={{ marginTop: 10 }}>
													<Radio value={true}>Ya</Radio>
													<Radio value={false}>Tidak</Radio>
												</Radio.Group>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									{marketingAspectForm.value?.isOtherBusiness && (
										<Fragment>
											<Row gutter={[20, 40]}>
												<Col span={12}>
													<Form.Item
														name={marketingAspectForm.fieldName(
															'otherBusiness',
														)}
														label="Sebutkan Usaha Lain"
														rules={[rule.required()]}
													>
														<Input />
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item
														name={marketingAspectForm.fieldName(
															'otherBusinessPortion',
														)}
														label="Porsi"
														rules={[rule.required()]}
													>
														<InputNumber suffix="%" />
													</Form.Item>
												</Col>
											</Row>
											<Divider />
										</Fragment>
									)}
								</Form>

								<CCAVisitReportCompetitor />
								<CCAVisitReportBouwheer
									data={{ data: { officeObservation } }}
									mainInformation={
										useCCAVisitReportVerificationPage()?.mainInformation
									}
								/>

								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={marketingAspectForm.form}
									name="marketingAspectNotes"
									initialValues={
										{
											marketingAspectNotes:
												marketingAspect?.marketingAspectNotes,
											marketingAspectVerificationNotes:
												marketingAspect?.marketingAspectVerificationNotes,
										} as MarketingAspect
									}
								>
									<Form.Item
										name={marketingAspectForm.fieldName('marketingAspectNotes')}
										label="FRM Notes"
										labelCol={{
											span: 6,
											style: {
												alignSelf: 'center',
											},
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
									>
										{marketingAspectForm.form.getFieldValue(
											marketingAspectForm.fieldName('marketingAspectNotes'),
										) ?? '-'}
									</Form.Item>
									<Divider />
									<Form.Item
										name={marketingAspectForm.fieldName(
											'marketingAspectVerificationNotes',
										)}
										label="Verification Notes"
										labelCol={{
											span: 6,
											style: {
												alignSelf: 'center',
											},
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
										rules={[rule.required()]}
									>
										<Input.TextArea rows={4} />
									</Form.Item>
								</Form>

								<Button
									type="primary"
									style={{
										width: 200,
										display: 'block',
										marginLeft: 'auto',
										marginTop: 30,
									}}
									loading={
										submitCCAOfficeObservationMarketingAspectMutation.isPending
									}
									onClick={() => marketingAspectForm.form.submit()}
								>
									SAVE
								</Button>
							</Form.Provider>
						),
					},
					{
						key: 'workshop',
						label: 'Workshop/Gudang',
						extra: renderCheck('workshop'),
						collapsible: !checkedItems.includes('marketingAspect')
							? 'disabled'
							: undefined,
						children: (
							<Form.Provider
								onFormFinish={async (name, { values }) => {
									if (!name) return;
									try {
										await submitCCAOfficeObservationWarehouseMutation.mutateAsync(
											{
												...(values as OfficeObservationWarehouse<'payload'>),
												surveyId: surveyInformation!.id!,
											},
										);
										handleChecked('workshop');
									} catch {
										notification.error({
											message: 'Failed',
											description: 'Failed submit workshop',
										});
									}
								}}
							>
								<CCAVisitReportWarehouse {...warehouseForm} />
								<CCAVisitReportSparePart />
								<CCAVisitReportInventory />

								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									name="materialOwnership"
									form={warehouseForm.form}
									labelCol={{
										span: 10,
									}}
									initialValues={
										{
											materialOwnershipTypeId:
												warehouse?.materialOwnershipType?.id,
											materialOwnershipPortion:
												warehouse?.materialOwnershipPortion,
										} as OfficeObservationWarehouse<'payload'>
									}
								>
									<Row gutter={[20, 40]}>
										<Col span={12}>
											<Form.Item
												name={warehouseForm.fieldName([
													'materialOwnershipTypeId',
												])}
												label="Kepemilikan Bahan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Select
													{...selectMaterialOwnershipType}
													options={
														!warehouse?.materialOwnershipType ||
														selectMaterialOwnershipType?.options?.length
															? selectMaterialOwnershipType.options
															: [
																	{
																		label:
																			warehouse.materialOwnershipType.value,
																		value: warehouse.materialOwnershipType.id,
																	},
																]
													}
												/>
											</Form.Item>
										</Col>
										{warehouseForm.value?.materialOwnershipTypeId ===
											OfficeMaterialOwnership.Konsinyasi && (
											<Col span={12}>
												<Form.Item
													name={warehouseForm.fieldName([
														'materialOwnershipPortion',
													])}
													label="Berapa % ?"
													labelCol={{ span: 10 }}
													rules={[rule.required()]}
												>
													<InputNumber min={0} />
												</Form.Item>
											</Col>
										)}
									</Row>
									<Divider />
								</Form>

								<CCAVisitReportParetoSupplier />
								<Typography.Title level={3}>
									List Piutang Pareto
								</Typography.Title>
								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={warehouseForm.form}
									name="warehouseNotes"
									initialValues={
										{
											totalReceivables: warehouse?.totalReceivables,
										} as OfficeObservationWarehouse<'payload'>
									}
								>
									<Row gutter={[20, 40]}>
										<Col span={12}>
											<Form.Item
												name={warehouseForm.fieldName('totalReceivables')}
												label="Total Piutang"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												initialValue={warehouse?.totalReceivables}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
								</Form>
								<CCAVisitReportParetoReceivable />

								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={warehouseForm.form}
									name="warehouseNotes"
									initialValues={
										{
											warehouseNotes: warehouse?.warehouseNotes,
											warehouseVerificationNotes:
												warehouse?.warehouseVerificationNotes,
										} as OfficeObservationWarehouse<'payload'>
									}
								>
									<Form.Item
										name={warehouseForm.fieldName('warehouseNotes')}
										label="FRM Notes"
										labelCol={{
											span: 5,
											style: {
												alignSelf: 'center',
											},
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
									>
										{warehouseForm.form.getFieldValue(
											warehouseForm.fieldName('warehouseNotes'),
										) ?? '-'}
									</Form.Item>
									<Divider />
									<Form.Item
										name={warehouseForm.fieldName('warehouseVerificationNotes')}
										label="Verification Notes"
										labelCol={{
											span: 5,
											style: {
												alignSelf: 'center',
											},
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
										rules={[rule.required()]}
									>
										<Input.TextArea rows={4} />
									</Form.Item>
								</Form>

								<Button
									type="primary"
									style={{
										width: 200,
										display: 'block',
										marginLeft: 'auto',
										marginTop: 30,
									}}
									loading={
										submitCCAOfficeObservationWarehouseMutation.isPending
									}
									onClick={() => warehouseForm.form.submit()}
								>
									SAVE
								</Button>
							</Form.Provider>
						),
					},
					{
						key: 'collateral',
						label: 'Agunan',
						collapsible: !checkedItems.includes('workshop')
							? 'disabled'
							: undefined,
						extra: renderCheck('collateral'),
						children: (
							<Form.Provider
								onFormFinish={async (name, { values }) => {
									if (!name) return;
									try {
										await submitCCAOfficeObservationCollateralAspectMutation.mutateAsync(
											values as OfficeObservationCollateralAspect,
										);
										handleChecked('collateral');
									} catch {
										notification.error({
											message: 'Failed',
											description: 'Failed submit collateral',
										});
									}
								}}
							>
								<CCAVisitReportOfficeCollateralUnit />

								<Form
									{...ANTD_DEFAULT_PROPS.FORM}
									form={collateralForm.form}
									name="collateralNotes"
									initialValues={
										{
											surveyId: surveyInformation?.id,
											collateralNotes: collateral?.collateralNotes,
											collateralVerificationNotes:
												collateral?.collateralVerificationNotes,
										} as OfficeObservationCollateralAspect
									}
								>
									<Form.Item
										name={collateralForm.fieldName('surveyId')}
										noStyle
									/>

									<Form.Item
										name={collateralForm.fieldName('collateralNotes')}
										label="Informasi Tambahan"
										labelCol={{
											span: 5,
											style: {
												alignSelf: 'center',
											},
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
										rules={[rule.required()]}
									>
										{collateralForm.form.getFieldValue(
											collateralForm.fieldName('collateralNotes'),
										)}
									</Form.Item>
									<Divider />
									<Form.Item
										name={collateralForm.fieldName(
											'collateralVerificationNotes',
										)}
										label="Verification Notes"
										labelCol={{
											span: 5,
											style: {
												alignSelf: 'center',
											},
										}}
										wrapperCol={{
											style: {
												marginLeft: -5,
											},
										}}
										rules={[rule.required()]}
									>
										<Input.TextArea rows={3} />
									</Form.Item>
								</Form>

								<Button
									type="primary"
									style={{
										width: 200,
										display: 'block',
										marginLeft: 'auto',
										marginTop: 30,
									}}
									loading={
										submitCCAOfficeObservationCollateralAspectMutation.isPending
									}
									onClick={() => {
										collateralForm.form.submit();
									}}
								>
									SAVE
								</Button>
							</Form.Provider>
						),
					},
				]}
				style={{
					marginBottom: 20,
				}}
			/>

			<CCAVisitReportPhoto />
			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 20,
				}}
				disabled={!isAllowSubmitting}
				onClick={() => {
					onNext?.();
				}}
			>
				NEXT
			</Button>
		</Fragment>
	);
};

export default CCAVisitReportVerificationOffice;
